import config from '@/config/index';
import { CustomException } from '@/lib/custom-exception';
import { logs } from '@shoppykey/tools';
import { request as fetch, history } from '@umijs/max';
import { notification } from 'antd';
import _ from 'lodash';
import qs from 'qs';
import { CustomExceptionTypes } from './custom-exception-types';
import { getToken, isLogin } from './token';
import { getPageQuery } from './utils';

const codeMessage: any = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '用户输入信息有误',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '记录不存在',
  405: '请求方法不被允许。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultHeader: any = {};

type ResponseType = 'json' | 'text' | 'blob';

/**
 * 请求参数
 */
type FetchOptions = {
  getResponse?: boolean;
  isPublic?: boolean;
  needAuth?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query?: any;
  responseType?: ResponseType;
  /**
   * 保持沉默
   */
  silent?: boolean;
};

/**
 * 请求参数
 */
type BodyFetchOptions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
} & FetchOptions;

type Options = {
  method: 'POST' | 'GET' | 'DELETE' | 'PUT';
  url: string;
} & BodyFetchOptions;

export function stringifyQuery(query: unknown) {
  return qs.stringify(query, {
    allowDots: true,
    arrayFormat: 'repeat',
    encoder: (f) => {
      if (typeof f === 'string') {
        return encodeURIComponent(f.trim());
      }
      return encodeURIComponent(f);
    },
  });
}

export function parseQuery(queryString: string = '') {
  const query = qs.parse(queryString.replace('?', ''));
  return query;
}

async function migrateHeader(option: Options) {
  const headers = { ...defaultHeader };
  if (!option.isPublic) {
    const isLoggedIn = isLogin();
    if (!isLoggedIn) {
      const { redirect } = getPageQuery();
      history.replace(
        `/auth/login?${qs.stringify({
          redirect: redirect || window.location.href,
        })}`,
      );
      // eslint-disable-next-line no-console
      console.trace('需要登陆');
      throw new CustomException(CustomExceptionTypes.NEED_LOGIN, '需要登陆');
    }
  }
  headers.Authorization = `Bearer ${getToken()}`;
  return {
    ...option,
    url: `${config.Host}${option.url}?${stringifyQuery(option.query)}`,
    headers,
  };
}

async function request(params: Options) {
  try {
    const res = await fetch(params.url, { ...params, getResponse: true });
    if (_.get(res.headers, 'Deprecated')) {
      logs.warn(`${params.url} 接口已经废弃，请及时修改`);
    }
    if (res.data && res.data.errorCode) {
      throw new CustomException(res.data.errorCode, res.data.errorMessage);
    }
    if (params.getResponse) {
      return res;
    }
    return res.data.data;
  } catch (e: any) {
    logs.error('出错了', e);
    if (_.has(e, 'response')) {
      const { status } = e.response;
      if (status === 401) {
        const { redirect } = getPageQuery();
        history.replace(
          `/auth/login?${qs.stringify({
            redirect: redirect || window.location.href,
          })}`,
        );
        throw new CustomException(CustomExceptionTypes.NEED_LOGIN, '需要登陆');
      }
      if (params.silent !== true) {
        const message = codeMessage[status] || '';
        const description = _.get(e, 'response.data.message');
        notification.error({
          message,
          description,
        });
      }
      throw new CustomException(CustomExceptionTypes.SYS_ERROR, e);
    } else {
      throw new CustomException(CustomExceptionTypes.UNKNOWN_ERROR, e);
    }
  }
}

export class Fetch {
  public static async post<T>(url: string, option?: BodyFetchOptions): Promise<T> {
    const params = await migrateHeader({
      method: 'POST',
      url,
      ...option,
    });
    return request(params);
  }

  public static async delete<T>(url: string, option?: BodyFetchOptions): Promise<T> {
    const params = await migrateHeader({
      method: 'DELETE',
      url,
      ...option,
    });
    return request(params);
  }

  public static async put<T>(url: string, option?: BodyFetchOptions): Promise<T> {
    const params = await migrateHeader({
      method: 'PUT',
      url,
      ...option,
    });
    return request(params);
  }

  public static async get<T>(url: string, option?: FetchOptions): Promise<T> {
    const params = await migrateHeader({
      method: 'GET',
      url,
      ...option,
    });
    return request({ ...params });
  }
}
